import Chip, { ChipSet } from "@/components/Chip";
import DateSelect from "@/components/DateSelect";
import Switch from "@/components/Switch";
import { QUICK_DUE_DATE_OPTIONS } from "@/features/global/components/AddTaskSideSheet/quickDueDate";

import { careerPlanFollowUpDueDate } from "./careerPlanDocument";

type CareerPlanFollowUpDueDate = { startDate: Date; endDate: Date };

type CareerPlanAttachTaskSectionProps = {
  attachTask: boolean;
  dueDate: CareerPlanFollowUpDueDate;
  disabled?: boolean;
  onToggle: (enabled: boolean) => void;
  onDueDateChange: (date: CareerPlanFollowUpDueDate) => void;
};

function calendarDayKey(date: Date) {
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

function matchingQuickDueDateDays(dueDate: CareerPlanFollowUpDueDate): number | null {
  const dueKey = calendarDayKey(dueDate.startDate);

  for (const option of QUICK_DUE_DATE_OPTIONS) {
    const expected = careerPlanFollowUpDueDate(new Date(), option.days).startDate;
    if (calendarDayKey(expected) === dueKey) return option.days;
  }

  return null;
}

export default function CareerPlanAttachTaskSection({
  attachTask,
  dueDate,
  disabled = false,
  onToggle,
  onDueDateChange
}: CareerPlanAttachTaskSectionProps) {
  const selectedDays = matchingQuickDueDateDays(dueDate);

  return (
    <div className="flex flex-col gap-4 pt-12" data-testid="career-plan-attach-task">
      <Switch
        disabled={disabled}
        headline="Attach a Task"
        selected={attachTask}
        supportingText="Create a follow-up task for this Career Plan."
        onChange={() => onToggle(!attachTask)}
      />
      {attachTask && (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center" data-testid="career-plan-task-due-date">
          <DateSelect
            label="Due Date"
            value={dueDate}
            onChange={(date) => {
              if (!date || typeof date === "string") return;
              const start = date.startDate instanceof Date ? date.startDate : null;
              if (!start) return;
              onDueDateChange({ startDate: start, endDate: start });
            }}
            placeholder="Select a date"
            useSingleDate
            useModal
            className="min-w-0 w-full sm:max-w-sm"
            disabled={disabled}
          />
          <div className="flex h-[56px] shrink-0 items-center">
            <ChipSet>
              {QUICK_DUE_DATE_OPTIONS.map((option) => (
                <Chip
                  key={option.days}
                  disabled={disabled}
                  label={option.label}
                  selected={selectedDays === option.days}
                  showCheckmark={false}
                  onClick={() => onDueDateChange(careerPlanFollowUpDueDate(new Date(), option.days))}
                />
              ))}
            </ChipSet>
          </div>
        </div>
      )}
    </div>
  );
}
